import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  executeWorkflowAction,
  getAvailableActions,
  getProposalStatusHistory,
  canViewProposal,
  getProjectProposals,
  canEditProposal
} from '../api/proposal-workflow-api';
import {
  ExecuteWorkflowActionRequest,
  WorkflowTransitionResult,
  ProposalStatusHistory,
  ProposalWorkflowAction,
  CanViewProposalResponse
} from '../models/ProposalWorkflow';
import { useSnackbarContext } from '../contexts/useSnackBarContext';
import { useTranslation } from 'react-i18next';

/**
 * Hook to execute workflow actions on a proposal
 */
export const useExecuteWorkflowAction = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation<WorkflowTransitionResult, Error, ExecuteWorkflowActionRequest>({
    mutationFn: executeWorkflowAction,
    onSuccess: async (data) => {
      // Wait for backend to process the workflow transition
      await new Promise(resolve => setTimeout(resolve, 800));

      // Cancel any ongoing queries to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['proposal-available-actions'] });
      await queryClient.cancelQueries({ queryKey: ['freelancer-proposal'] });
      await queryClient.cancelQueries({ queryKey: ['project-proposals'] });

      // Remove cached data and force immediate refetch
      queryClient.removeQueries({ queryKey: ['proposal-available-actions'] });

      // Invalidate and refetch all proposal-related queries
      await queryClient.refetchQueries({
        queryKey: ['freelancer-proposal'],
        type: 'all'
      });
      await queryClient.refetchQueries({
        queryKey: ['project-proposals'],
        type: 'all'
      });
      await queryClient.refetchQueries({
        queryKey: ['proposal-available-actions'],
        type: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['proposal-status-history'],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['proposal-conversations'],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['proposal-can-edit'],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['proposal-can-view'],
        refetchType: 'all'
      });

      // Invalidate project-related queries
      await queryClient.invalidateQueries({
        queryKey: ['project', data.projectId],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['projectBrief', data.projectId],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['projectFreelancerProfiles'],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['projects'],
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['myProjects'],
        refetchType: 'all'
      });

      if (data.isSuccess) {
        showSuccess(
          data.message || t('proposal.workflow.actionSuccess')
        );
      } else {
        showError(
          data.errorMessage || t('proposal.workflow.actionFailed')
        );
      }
    },
    onError: (error) => {
      showError(
        error.message || t('proposal.workflow.actionError')
      );
    }
  });
};

/**
 * Hook to get available workflow actions for a proposal
 */
export const useAvailableActions = (proposalId: number, enabled = true) => {
  return useQuery<ProposalWorkflowAction[], Error>({
    queryKey: ['proposal-available-actions', proposalId],
    queryFn: () => getAvailableActions(proposalId),
    enabled: enabled && !!proposalId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true, 
  });
};

/**
 * Hook to get status history for a proposal
 */
export const useProposalStatusHistory = (proposalId: number, enabled = true) => {
  return useQuery<ProposalStatusHistory[], Error>({
    queryKey: ['proposal-status-history', proposalId],
    queryFn: () => getProposalStatusHistory(proposalId),
    enabled: enabled && !!proposalId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true, 
  });
};

/**
 * Hook to check if current user can view a proposal
 */
export const useCanViewProposal = (proposalId: number, enabled = true) => {
  return useQuery<CanViewProposalResponse, Error>({
    queryKey: ['proposal-can-view', proposalId],
    queryFn: () => canViewProposal(proposalId),
    enabled: enabled && !!proposalId,
    staleTime: 0, 
    gcTime: 0,
  });
};

/**
 * Hook to get all proposals for a project (CSM/Admin view)
 */
export const useProjectProposals = (projectId: number, enabled = true) => {
  return useQuery<any[], Error>({
    queryKey: ['project-proposals', projectId],
    queryFn: () => getProjectProposals(projectId),
    enabled: enabled && !!projectId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true, 
  });
};

/**
 * Hook to check if current user can edit a proposal
 */
export const useCanEditProposal = (proposalId: number | undefined, enabled = true) => {
  return useQuery<boolean, Error>({
    queryKey: ['proposal-can-edit', proposalId],
    queryFn: () => canEditProposal(proposalId!),
    enabled: enabled && !!proposalId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
};